import React from 'react';
import DashboardOutlined from '@material-ui/icons/DashboardOutlined';
import LocalShippingOutlined from '@material-ui/icons/LocalShippingOutlined';
import ShoppingBasketOutlined from '@material-ui/icons/ShoppingBasketOutlined';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import { PharmacyStatistics } from '../../pages';
import treetLogo from '../../images/default-pharmacy-logo.svg';
import { pharmacyAppUrl } from '../../utils/pharmacyUrl';

const useStyles = makeStyles(theme => ({
  pharmacyName: {
    margin: theme.spacing(0),
  },
  address: {
    lineHeight: '1rem',
    margin: theme.spacing(1, 0),
  },
  container: {
    margin: theme.spacing(2, 0.5),
    boxShadow: '0px 4px 14px rgba(0, 0, 0, 0.17)',
    width: '100%',
    height: 160,
    borderRadius: theme.spacing(2),
    cursor: 'pointer',
  },
  content: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5, 2),
  },
  logoContainer: {
    flex: '0 0 70px',
    height: '70px',
    padding: theme.spacing(1),
    marginLeft: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: theme.spacing(2),
  },
  logo: {
    maxWidth: '62px',
    maxHeight: '62px',
  },
  footer: {
    borderTop: '1px dashed',
    borderColor: theme.palette.grey.A100,
  },
  footerContainer: {
    padding: theme.spacing(0.5, 2),
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  footerItem: {
    display: 'flex',
    alignItems: 'center',
    paddingTop: theme.spacing(1),
    marginRight: theme.spacing(1.5),
  },
  footerItemIcon: {
    fontSize: '16px',
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(0.5),
    [theme.breakpoints.up('md')]: {
      fontSize: '20px',
    },
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  pharmacyLink: {
    display: 'flex',
    alignItems: 'center',
    textDecoration: 'none !important',
    '&:hover': { textDecoration: 'underline !important' },
  },
}));

type Props = {
  pharmacies: PharmacyStatistics[];
};

const Pharmacy: React.FunctionComponent<Props> = ({ pharmacies }) => {
  const classes = useStyles();

  return (
    <Grid container spacing={2}>
      {pharmacies.map(pharmacy => (
        <Grid item xs={12} md={6} key={pharmacy.url || pharmacy.name}>
          <Card
            className={classes.container}
            // City listing pages link straight to the app webshop, not treet.fi detail pages.
            onClick={() => {
              window.location.href = pharmacyAppUrl(pharmacy);
            }}
          >
            <div className={classes.cardContent}>
              <CardContent className={classes.content}>
                <Box display="flex" flexDirection="column">
                  <Typography align="left" variant="h6" className={classes.pharmacyName}>
                    {pharmacy.name}
                  </Typography>

                  <Typography variant="body2" align="left" color="textSecondary" className={classes.address}>
                    {pharmacy.street_address}, {pharmacy.city}
                  </Typography>
                </Box>
                <Card variant="outlined" className={classes.logoContainer}>
                  <img className={classes.logo} src={pharmacy.logo_url ?? treetLogo} alt={`${pharmacy.name} logo`} />
                </Card>
              </CardContent>

              <CardContent className={classes.footer} style={{ padding: 0 }}>
                <div className={classes.footerContainer}>
                  {pharmacy.has_webshop && (
                    <div className={classes.footerItem}>
                      <a
                        href={pharmacy.url ?? ''}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={classes.pharmacyLink}
                        onClick={e => e.stopPropagation()}
                      >
                        <ShoppingBasketOutlined color="disabled" className={classes.footerItemIcon} />
                        <Typography component="span" variant="caption" color="textSecondary">
                          Verkkoapteekki
                        </Typography>
                      </a>
                    </div>
                  )}

                  {pharmacy.offers_delivery && (
                    <div className={classes.footerItem}>
                      <LocalShippingOutlined
                        color="disabled"
                        className={classes.footerItemIcon}
                        data-testid="offers-delivery-icon"
                      />
                      <Typography component="span" variant="caption" color="textSecondary">
                        Kotiinkuljetus
                      </Typography>
                    </div>
                  )}

                  {pharmacy.offers_pickup_box && (
                    <div className={classes.footerItem}>
                      <DashboardOutlined color="disabled" className={classes.footerItemIcon} />
                      <Typography component="span" variant="caption" color="textSecondary">
                        Noutolokerikko
                      </Typography>
                    </div>
                  )}
                </div>
              </CardContent>
            </div>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

export default Pharmacy;
